import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../services/apiClient';
import { useI18n } from '../../../contexts/I18nContext';
import { FirmwareItem } from '../../../types/otaUpdate';
import { useFirmwareOptions } from '../useFirmwareOptions';
import { FirmwareFormData, buildFirmwarePayload, emptyFirmwareForm, explainFirmwareError, firmwareToForm } from '../firmwareForm';

interface Options {
  firmware: FirmwareItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}

/**
 * The firmware dialog: read the row, switch to editing, save or delete.
 * Opening (or receiving another row) resets the form to the row's values.
 */
export const useFirmwareDetail = ({ firmware, isOpen, onClose, onSave, onDelete }: Options) => {
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);
  const { models, clients } = useFirmwareOptions(isOpen);

  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [form, setForm] = useState<FirmwareFormData>(emptyFirmwareForm);

  useEffect(() => {
    if (!firmware || !isOpen) return;
    setForm(firmwareToForm(firmware));
    setEditing(false);
    setError(null);
    setConfirmingDelete(false);
  }, [firmware, isOpen]);

  const setField = (field: keyof FirmwareFormData, value: string | boolean) => setForm((prev) => ({ ...prev, [field]: value }));

  const cancelEdit = () => {
    setEditing(false);
    if (firmware) setForm(firmwareToForm(firmware));
  };

  const save = async () => {
    if (!firmware) return;
    setBusy(true);
    setError(null);
    try {
      await axios.patch(`/v2/firmware/${firmware.id}`, buildFirmwarePayload(form));
      setEditing(false);
      onSave();
    } catch (err: any) {
      console.error('Failed to update firmware:', err);
      setError(explainFirmwareError(err, t('otaUpdates.updateFailed')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!firmware) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/v2/firmware/${firmware.id}`);
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Failed to delete firmware:', err);
      setError(explainFirmwareError(err, t('otaUpdates.deleteFailed')));
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  /** The client's name, or "all clients" for a global firmware. */
  const clientName = (clientId: string | null): string => {
    if (!clientId) return t('otaUpdates.allClientsGlobal');
    return clients.find((c) => c.id === clientId)?.name || clientId;
  };

  return {
    form, editing, busy, error, dismissError: () => setError(null), confirmingDelete, setConfirmingDelete, models, clients, clientName,
    setField, startEdit: () => setEditing(true), cancelEdit, save, remove,
  };
};

export type FirmwareDetailState = ReturnType<typeof useFirmwareDetail>;
